import { useStore } from "../../contexts/StoreContext";
import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { createPage, updatePage } from "../../api/mutations";
import { fetchPages } from "../../api/queries";
import ClubBuilder from "../../components/ClubBuilder/ClubBuilder";

// Function to generate default club data with store colors
const generateDefaultClubData = (selectedStore) => {
    const backgroundColor = selectedStore?.background_color || "#000000";
    const primaryColor = selectedStore?.primary_color || "#ffffff";
    const secondaryColor = selectedStore?.secondary_color || "#000000";

    return {
        header: {
            text_color: primaryColor,
            background_color: backgroundColor,
            navigation: [
                {
                    name: "Home",
                    href: "/"
                },
                {
                    name: "How it works",
                    href: "/how-it-works"
                },
                {
                    name: "Campaigns",
                    href: "/campaigns"
                }
            ],
            login_text: "login text"
        },
        hero: {
            title: "main title",
            description: "main description",
            text_color: backgroundColor,
            background_color: primaryColor,
            button: {
                text: "button text",
                href: "button href",
                text_color: primaryColor,
                background_color: backgroundColor
            }
        },
        call_to_action: {
            content: "content",
            text_color: primaryColor,
            background_color: backgroundColor,
        },
        how_it_works: {
            title: "how it works title",
            description: "how it works description",
            text_color: backgroundColor,
            background_color: primaryColor,
            steps: [
                {
                    title: "step 1",
                    description: "step 1 description",
                    image_url: "step 1 image url"
                },
                {
                    title: "step 2",
                    description: "step 2 description",
                    image_url: "step 2 image url"
                },
                {
                    title: "step 3",
                    description: "step 3 description",
                    image_url: "step 3 image url"
                }
            ]
        },
        benefits: {
            content: "content",
            text_color: primaryColor,
            background_color: backgroundColor,
        },
        campaign: {
            title: "campaigns title",
            description: "campaigns description",
            text_color: backgroundColor,
            background_color: primaryColor,
            items: [
                {
                    title: "campaign 1",
                    description: "campaign 1 description",
                    image_url: "campaign 1 image url"
                },
                {
                    title: "campaign 2",
                    description: "campaign 2 description",
                    image_url: "campaign 2 image url"
                },
                {
                    title: "campaign 3",
                    description: "campaign 3 description",
                    image_url: "campaign 3 image url"
                },
                {
                    title: "campaign 4",
                    description: "campaign 4 description",
                    image_url: "campaign 4 image url"
                }
            ]
        },
        footer: {
            text_color: primaryColor,
            background_color: backgroundColor
        }
    };
};

export default function NewClub() {
    const { selectedStore } = useStore();
    const [selectedPage, setSelectedPage] = useState(null);
    const [pageData, setPageData] = useState(generateDefaultClubData(selectedStore));
    const selectedPageRef = useRef(selectedPage);
    const [loaded, setLoaded] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        selectedPageRef.current = selectedPage;
    }, [selectedPage]);

    // Update pageData when selectedStore changes
    useEffect(() => {
        setPageData(generateDefaultClubData(selectedStore));
    }, [selectedStore]);

    // Warn user on refresh/close if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!hasUnsavedChanges) return;
            e.preventDefault();
            e.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const { data: pages, isLoading } = useQuery({
        queryKey: ["pages", selectedStore?.id],
        queryFn: () => fetchPages(selectedStore?.id),
        enabled: !!selectedStore?.id,
    });

    // Clean function to merge backend data with default structure
    const normalizeData = (data) => {
        try {
            const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
            return { ...generateDefaultClubData(selectedStore), ...parsedData };
        } catch (error) {
            console.error("Error parsing data:", error);
            return generateDefaultClubData(selectedStore);
        }
    };

    useEffect(() => {
        if (!pages) return;
        if (loaded) return;

        if (pages.length > 0) {
            console.log("pages[0] in useEffect:", pages[0]);
            setSelectedPage(pages[0]);
            try {
                const parsedData = normalizeData(pages[0].data);
                setPageData(parsedData);
                setLoaded(true);
            } catch (error) {
                console.error("Error parsing page data:", error);
                setPageData(generateDefaultClubData(selectedStore));
            }
        } else {
            setSelectedPage(null);
            setPageData(generateDefaultClubData(selectedStore));
        }
    }, [pages, selectedStore]);

    const createPageMutation = useMutation({
        mutationFn: createPage,
        onSuccess: (data) => {
            console.log("data in onSuccess:", data);
            setSelectedPage(data);
            setHasUnsavedChanges(false);
            navigate("/club");
        },
    });

    const updatePageMutation = useMutation({
        mutationFn: updatePage,
        onSuccess: () => {
            setHasUnsavedChanges(false);
            navigate("/club");
        },
    });

    const handleDataChange = (newData) => {
        setPageData(newData);
        setHasUnsavedChanges(true);
    };

    const handleSave = (data) => {
        console.log("selectedStore in handleSave:", selectedStore);
        console.log("selectedStore?.id in handleSave:", selectedStore?.id);
        if (!selectedStore?.id) {
            console.warn("No store selected, cannot save page.");
            return;
        }

        const currentPage = selectedPageRef.current;
        console.log("selectedPage in handleSave:", currentPage);

        if (!currentPage) {
            createPageMutation.mutate({
                storeId: selectedStore?.id,
                pageData: {
                    name: "Loyaltify Club",
                    data: data,
                    html: "", // You can generate HTML here if needed
                },
            });
        } else {
            updatePageMutation.mutate({
                storeId: selectedStore?.id,
                pageId: currentPage.id,
                pageData: {
                    name: "Loyaltify Club",
                    data: data,
                    html: "", // You can generate HTML here if needed
                },
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="w-full h-full">
            {hasUnsavedChanges && (
                <div className="sticky top-0 z-10 w-full bg-yellow-50 border-b border-yellow-200 text-yellow-800 px-4 py-2 text-sm">
                    You have unsaved changes. Refreshing will discard them.
                </div>
            )}
            {selectedStore?.id ? (
                <ClubBuilder
                    data={pageData}
                    onChange={handleDataChange}
                    onSave={handleSave}
                    selectedStore={selectedStore}
                />
            ) : (
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Store Selected</h2>
                        <p className="text-gray-600">Please select a store to edit the club page</p>
                    </div>
                </div>
            )}
        </div>
    );
}
